import { useEffect, useRef } from 'react';
import { toast } from '@/components/ui/use-toast';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: string };
    return data.version ?? null;
  } catch {
    return null;
  }
}

export function useVersionCheck() {
  const notifiedRef = useRef(false);

  useEffect(() => {
    const currentVersion = import.meta.env.VITE_APP_VERSION;
    if (!currentVersion) return;

    const check = async () => {
      if (notifiedRef.current) return;
      const deployedVersion = await fetchDeployedVersion();
      if (!deployedVersion || deployedVersion === currentVersion) return;
      notifiedRef.current = true;
      toast({
        title: 'A new version is available',
        description: 'Refresh the page to load the latest version.',
        sticky: true,
        action: {
          label: 'Refresh',
          altText: 'Refresh the page',
          onClick: () => window.location.reload(),
        },
      });
    };

    const interval = setInterval(check, POLL_INTERVAL_MS);
    window.addEventListener('focus', check);
    check();

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', check);
    };
  }, []);
}
